/**
 * Index Usage Section Component
 *
 * Displays PostgreSQL index usage statistics with usage score visualization.
 * Helps identify unused or underutilized indexes for database optimization.
 *
 * Features:
 * - Usage score with color-coded progress bars
 * - Sortable columns (table, index, scans, size, usage)
 * - Filter for unused indexes only
 * - Filter by minimum size
 * - Optimization recommendations for unused indexes
 * - Uses Shadcn/ui components exclusively
 * - WCAG 2.1 Level AA accessible
 */

import { useState, useMemo } from 'react';
import { useIndexUsage } from '../../../hooks/admin';
import type { IndexUsageStats } from '../../../hooks/admin/types/database';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  AlertTriangle,
  Info,
  RefreshCw,
  Zap,
} from 'lucide-react';

interface IndexUsageSectionProps {
  /** Optional table name filter */
  tableName?: string;
  /** Optional class name */
  className?: string;
}

type SortField = 'table_name' | 'index_name' | 'scans' | 'tuples_read' | 'tuples_fetched' | 'size_bytes' | 'usage_score';
type SortDirection = 'asc' | 'desc';

/**
 * Loading skeleton for the index usage section
 */
function IndexUsageSkeleton() {
  return (
    <Card data-testid="index-usage-skeleton">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-6 w-24" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Get usage score color class based on score percentage
 */
function getUsageScoreColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-blue-500';
  if (score >= 20) return 'bg-yellow-500';
  return 'bg-red-500';
}

/**
 * Get usage score badge variant
 */
function getUsageScoreBadgeVariant(score: number): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (score >= 80) return 'default';
  if (score >= 50) return 'secondary';
  if (score >= 20) return 'outline';
  return 'destructive';
}

/**
 * Get usage score label
 */
function getUsageScoreLabel(score: number): string {
  if (score >= 80) return 'Heavily Used';
  if (score >= 50) return 'Moderately Used';
  if (score >= 20) return 'Rarely Used';
  return 'Unused';
}

/**
 * Index Usage Section - Main component
 *
 * Displays PostgreSQL index usage statistics to help identify unused
 * or underutilized indexes that could be removed to improve write performance.
 */
export function IndexUsageSection({ tableName, className }: IndexUsageSectionProps) {
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [minSizeMB, setMinSizeMB] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('usage_score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { data, isLoading, isError, mutate } = useIndexUsage({
    table_name: tableName,
    unused_only: showUnusedOnly,
    min_size_mb: minSizeMB ? parseFloat(minSizeMB) : undefined,
    limit: 100,
  });

  // Filter and sort indexes
  const filteredAndSortedIndexes = useMemo(() => {
    if (!data?.indexes) return [];

    let result = [...data.indexes];

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (idx) =>
          idx.table_name.toLowerCase().includes(lowerSearch) ||
          idx.index_name.toLowerCase().includes(lowerSearch)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case 'table_name':
          aValue = a.table_name;
          bValue = b.table_name;
          break;
        case 'index_name':
          aValue = a.index_name;
          bValue = b.index_name;
          break;
        case 'scans':
          aValue = a.scans;
          bValue = b.scans;
          break;
        case 'tuples_read':
          aValue = a.tuples_read;
          bValue = b.tuples_read;
          break;
        case 'tuples_fetched':
          aValue = a.tuples_fetched;
          bValue = b.tuples_fetched;
          break;
        case 'size_bytes':
          aValue = a.size_bytes;
          bValue = b.size_bytes;
          break;
        case 'usage_score':
          aValue = a.usage_score;
          bValue = b.usage_score;
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return result;
  }, [data?.indexes, searchTerm, sortField, sortDirection]);

  // Handle column sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Render sort icon
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1 inline" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1 inline" />
    );
  };

  // Show skeleton during loading
  if (isLoading) {
    return <IndexUsageSkeleton />;
  }

  // Show error state
  if (isError || !data) {
    return (
      <Card data-testid="index-usage-error" className={className}>
        <CardHeader>
          <CardTitle>Index Usage Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load index usage statistics. {isError?.message || 'Please try again.'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="index-usage-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-5 w-5 text-primary" aria-hidden="true" />
              <CardTitle>Index Usage Statistics</CardTitle>
            </div>
            <CardDescription>
              Analyze index usage patterns to identify optimization opportunities
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{data.total_count} indexes</Badge>
            {data.unused_count > 0 && (
              <Badge variant="destructive">
                {data.unused_count} unused ({data.unused_size})
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/50 rounded-lg">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="index-search" className="sr-only">
              Search indexes
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="index-search"
                placeholder="Search table or index name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Min Size Filter */}
          <div className="w-32">
            <label htmlFor="min-size" className="sr-only">
              Min size (MB)
            </label>
            <Input
              id="min-size"
              type="number"
              placeholder="Min MB"
              value={minSizeMB}
              onChange={(e) => setMinSizeMB(e.target.value)}
              min={0}
              step={0.1}
            />
          </div>

          {/* Unused Only Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="unused-only"
              checked={showUnusedOnly}
              onChange={(e) => setShowUnusedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
            />
            <label htmlFor="unused-only" className="text-sm cursor-pointer">
              Unused only
            </label>
          </div>

          {/* Refresh Button */}
          <Button variant="outline" size="icon" onClick={() => mutate()}>
            <RefreshCw className="h-4 w-4" />
            <span className="sr-only">Refresh</span>
          </Button>
        </div>

        {/* Info Note */}
        {data.note && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{data.note}</AlertDescription>
          </Alert>
        )}

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          {filteredAndSortedIndexes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No indexes found matching the current filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    onClick={() => handleSort('table_name')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    Table
                    <SortIcon field="table_name" />
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort('index_name')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    Index Name
                    <SortIcon field="index_name" />
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort('scans')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors text-right"
                  >
                    Scans
                    <SortIcon field="scans" />
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort('tuples_read')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors text-right hidden lg:table-cell"
                  >
                    Tuples Read
                    <SortIcon field="tuples_read" />
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort('size_bytes')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors text-right"
                  >
                    Size
                    <SortIcon field="size_bytes" />
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort('usage_score')}
                    className="cursor-pointer hover:bg-muted/50 transition-colors text-center"
                  >
                    Usage Score
                    <SortIcon field="usage_score" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedIndexes.map((index: IndexUsageStats) => (
                  <TableRow key={`${index.table_name}.${index.index_name}`}>
                    <TableCell className="font-mono text-sm">
                      {index.table_name}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <span
                        title={`Tuples Read: ${index.tuples_read.toLocaleString()} | Tuples Fetched: ${index.tuples_fetched.toLocaleString()}`}
                        className="cursor-help border-b border-dotted border-muted-foreground"
                      >
                        {index.index_name}
                      </span>
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        index.scans === 0 ? 'text-red-600 font-medium' : ''
                      }`}
                    >
                      {index.scans.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground hidden lg:table-cell">
                      {index.tuples_read.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {index.size}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${getUsageScoreColor(
                              index.usage_score
                            )}`}
                            style={{ width: `${index.usage_score}%` }}
                            role="progressbar"
                            aria-valuenow={index.usage_score}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Usage score: ${index.usage_score}%`}
                          />
                        </div>
                        <Badge
                          variant={getUsageScoreBadgeVariant(index.usage_score)}
                          className="text-xs"
                        >
                          {getUsageScoreLabel(index.usage_score)}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Results Summary */}
        <div className="text-sm text-muted-foreground text-center">
          Showing {filteredAndSortedIndexes.length} of {data.total_count} indexes
        </div>

        {/* Optimization Recommendation */}
        {data.unused_count > 0 && (
          <Alert className="bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-800 dark:text-yellow-200">
              Optimization Opportunity
            </AlertTitle>
            <AlertDescription className="text-yellow-700 dark:text-yellow-300">
              Found {data.unused_count} unused index{data.unused_count !== 1 ? 'es' : ''} consuming{' '}
              <strong>{data.unused_size}</strong>. Consider removing unused indexes to improve
              write performance and reduce storage costs. Always verify in a staging
              environment before removing indexes from production.
            </AlertDescription>
          </Alert>
        )}

        {/* Help Text */}
        <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
          <p>
            <strong>Usage Score:</strong> Calculated based on index scans relative to table size.
            Lower scores indicate indexes that may be candidates for removal.
          </p>
          <p>
            <strong>Tip:</strong> Click column headers to sort. Unused indexes consume storage
            and slow down write operations.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
